import React from 'react';
import { toastr } from 'react-redux-toastr';
import CircularProgress from '@material-ui/core/CircularProgress';
import _ from 'lodash';

import moment from 'moment-timezone';
import { User, UtmParams } from "../types";


interface Studio {
  bookFrom: number;
  bookTo: number;
}

interface Funnel {
  saleStepsConfigs?: {
    _id: string;
    label: string;
  }[];
}

interface LoaderParams {
  center?: boolean;
}

interface SnackbarOptions {
  variant: string;
  anchorOrigin: {
    vertical: string;
    horizontal: string;
  };
}

type EnqueueSnackbar = (message: string, options: SnackbarOptions) => void;

export default class LayoutHelper {
  static getUtm(query: Record<string, string>): void {
    const utm_source = query.utm_source;
    if (utm_source) {
      const utm_medium = query.utm_medium;
      const utm_campaign = query.utm_campaign;
      const utm_term = query.utm_term;
      const utm_content = query.utm_content;
      const utm: UtmParams = {
        utm_source: utm_source,
        utm_medium: utm_medium,
        utm_campaign: utm_campaign,
        utm_term: utm_term,
        utm_content: utm_content,
      };
      localStorage.setItem('utm', JSON.stringify(utm));
    }
  }

  static isMobile(): boolean {
    return window.innerWidth <= 736;
  }

  static isEmailValid(email: string): boolean {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,8}$/i.test(email);
  }

  static validTag(tag?: string): string | undefined {
    if (tag) {
      return tag.replace(/[!@#$%^&*(),.?":{}|<>/\\]/g, '');
    }
    return undefined;
  }

  static getTimeInZone(timezone: string, dateTime: string | Date): moment.Moment {
    const utcOffset = moment.tz(timezone).utcOffset() / 60;
    const res = moment.utc(dateTime).add(utcOffset, 'hours');
    return res;
  }

  static isClassBookable(timezone: string, studio: Studio, classDateTime: moment.Moment): boolean {
    const utcOffset = moment.tz(timezone).utcOffset() / 60;

    let bookFrom = moment
      .utc()
      .add(studio.bookFrom + utcOffset, 'hour')
      .startOf('hour');
    if (classDateTime.diff(bookFrom) < 0) {
      return false;
    }

    let bookTo = moment
      .utc()
      .add(studio.bookTo + utcOffset, 'hour')
      .startOf('hour');
    if (classDateTime.diff(bookTo) > 0) {
      return false;
    }
    return true;
  }

  static renderLoader(params?: LoaderParams): React.ReactElement {
    let style: React.CSSProperties = {};
    if (params && params.center) {
      style = {
        position: 'fixed',
        left: '50%',
        top: '50%',
      };
    }
    return (<CircularProgress style={style} />);
  }

  static getPaymentPurpose(): string[] {
    return [
      'ATTENDANCE',
      'SERVICE',
      'RATE_PLAN',
      'DISCOUNT',
      'REIMBURSMENT',
      'SALARY',
    ];
  }

  static getSaleStageName(index: string | number, funnel: Funnel | undefined, translate: (key: string) => string): string {
    if (!funnel) {
      return translate('pendingClients.NO_FUNNEL');
    }
    const saleStepsConfigs = funnel?.saleStepsConfigs;
    if (saleStepsConfigs) {
      let stage = saleStepsConfigs.find((step) => String(step._id) === String(index));

      if (!stage && !isNaN(Number(index)) && saleStepsConfigs[Number(index)]) {
        stage = saleStepsConfigs[Number(index)];
      }

      return stage?.label || '';
    } else {
      return '';
    }
  }

  static getPaymentSources(): string[] {
    return ['CASH', 'CARD'];
  }

  static getFunnelStatuses(): string[] {
    return ['FAILED', 'ACTIVE', 'SUCCESS'];
  }

  static getSourceLabel(source: string | null, translate: (key: string) => string, owner?: User): string {
    if (source == null || source === 'null') {
      return translate('billing.NO_SOURCE');
    } else {
      if (source === 'DEPOSIT' && owner && owner.depositName) {
        return owner.depositName;
      }
      return this.getPaymentSources().includes(source)
        ? translate('billing.' + source)
        : source;
    }
  }

  static getPurposeLabel(purpose: string | null, translate: (key: string) => string): string {
    if (purpose == null || purpose === 'null' || purpose === 'NO_TYPE') {
      return translate('billing.NO_TYPE');
    } else {
      return this.getPaymentPurpose().includes(purpose)
        ? translate('billing.' + purpose)
        : purpose;
    }
  }

  static getBalanceColor(balance: number): string {
    if (balance > 0) {
      return 'green';
    } else {
      return 'red';
    }
  }

  static getDepositName(owner: User, translate: (key: string) => string): string {
    if (owner.depositName) {
      return owner.depositName;
    } else {
      return translate('myClients.deposit');
    }
  }

  static sortFunc(a: string | null, b: string | null, order: 'asc' | 'desc'): number {
    const koef = order === 'desc' ? 1 : -1;
    if (a) {
      return a.localeCompare(b || '') * koef;
    } else {
      return a === b ? 0 : koef;
    }
  }

  static isOwnerPayable(owner?: User): boolean | undefined {
    if (owner) {
      return !!(
        owner.liqPayPayment ||
        owner.wayForPayPayment ||
        owner.organizationSettings?.stripeConfigs?.apiKey
      );
    }
    return undefined;
  }

  static roundNumber(number?: number): number | undefined {
    if (number !== undefined && !isNaN(number)) {
      return Math.round(number * 100) / 100;
    }
    return undefined;
  }

  static isEmptyError(value: any): boolean {
    if (Array.isArray(value) && value.length === 1) {
      return _.isEmpty(value[0]);
    }
    return _.isEmpty(value);
  }

  static onSuccess(message: string): void {
    toastr.success(message, { timeOut: 3000 });
  }

  static onInfo(message: string): void {
    toastr.info(message, { timeOut: 3000 });
  }

  static onErrorWithMessage(error: { message?: string } | any): void {
    if (error?.message) {
      toastr.error(error.message);
    } else if (LayoutHelper.isEmptyError(error)) {
      console.log('Empty error response', error);
    } else {
      toastr.error(JSON.stringify(error));
    }
  }

  static onBulkSuccess(message: string): void {
    toastr.success(message, { timeOut: 4000 });
  }

  static onPartialFailure(message: string): void {
    toastr.warning(message, { timeOut: 6000 });
  }

  static onErr(err: { status?: number; statusText?: string } | any): void {
    if (err?.status === 504) {
      toastr.warning(err?.statusText);
    } else if (err?.statusText) {
      toastr.error(err?.statusText);
    } else if (LayoutHelper.isEmptyError(err)) {
      console.log('Empty error response', err);
    } else {
      toastr.error(JSON.stringify(err));
    }
  }

  static onErrSnack(enqueueSnackbar: EnqueueSnackbar): (err: any) => void {
    return (err) => {
      if (err?.status === 504) {
        enqueueSnackbar(err?.statusText, {
          variant: 'warning',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        });
      } else if (err?.statusText) {
        enqueueSnackbar(err?.statusText, {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        });
      } else {
        console.log(err);
        enqueueSnackbar(err, {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        });
      }
    };
  }

  static guid(): string {
    function s4(): string {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }

    return (s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4());
  }

  static createClassDateTime(date: string | Date, time: string | Date): moment.Moment {
    let startDate = moment.utc(date);
    let timeMoment = moment.utc(time);
    return startDate
      .set({
        hour: timeMoment.get('hour'),
        minute: timeMoment.get('minute'),
      })
      .startOf('minute');
  }

  static async copyToClickboad(link: string): Promise<void> {
    const args = { url: link };
    if (navigator.share) {
      return navigator.share(args).catch(console.log);
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(link);
    }
  }

  static onWarningWithMessage(error: { message?: string } | any): void {
    if (error?.message) {
      toastr.warning(error.message);
    } else {
      toastr.warning(JSON.stringify(error));
    }
  }
}
